import { type JsonLd } from 'json-ld-types';

export const REVALIDATION_BATCH_SIZE: number = 50;

const MAX_RETRY_AFTER_MS = 300_000;

export interface RevalidationResult {
  requested: number;
  changed: string[];
  unchanged: number;
  failedUrls: string[];
  retryAfterMs: number | undefined;
}

export const createRevalidationResult = (): RevalidationResult => ({
  requested: 0,
  changed: [],
  unchanged: 0,
  failedUrls: [],
  retryAfterMs: undefined,
});

export const mergeRevalidationResult = (
  target: RevalidationResult,
  source: RevalidationResult,
): void => {
  target.requested += source.requested;
  target.changed.push(...source.changed);
  target.unchanged += source.unchanged;
  target.failedUrls.push(...source.failedUrls);
  if (source.retryAfterMs !== undefined) {
    target.retryAfterMs = Math.max(target.retryAfterMs ?? 0, source.retryAfterMs);
  }
};

export const parseRetryAfterMs = (header: string | null): number | undefined => {
  if (!header) {
    return undefined;
  }
  const seconds = Number(header);
  if (!Number.isFinite(seconds) || seconds < 0) {
    return undefined;
  }
  return Math.min(seconds * 1000, MAX_RETRY_AFTER_MS);
};

const renderedDataFields = [
  'name',
  'summary',
  'tag',
  'taskType',
  'icon',
  'atlassian:priority',
  'atlassian:assignedTo',
  'atlassian:attributedTo',
  'atlassian:storyPoints',
  'atlassian:isCompleted',
  'url',
] as const;

const renderedMetaFields = [
  'access',
  'visibility',
  'containerId',
  'objectId',
  'whiteboards:previewPanelSupported',
] as const;

export const getRenderedFieldsSignature = (response: JsonLd.Response): string => {
  const data = (response.data ?? {}) as Record<string, unknown>;
  const meta = (response.meta ?? {}) as unknown as Record<string, unknown>;
  return JSON.stringify([
    renderedDataFields.map((field) => data[field] ?? null),
    renderedMetaFields.map((field) => meta[field] ?? null),
  ]);
};
